import { useMemo, useState } from 'react';
import { DateTime } from 'luxon';

import { Button } from '@/components/Button';
import { Field } from '@/components/Field';
import { Sheet } from '@/components/Sheet';
import { SNAP_MINUTES } from '@/lib/motion';
import { formatDuration } from '@/features/tasks/tree';
import type { Task } from '@/features/tasks/types';

interface Props {
  task: Task;
  timezone: string;
  onClose: () => void;
  onSubmit: (startsAt: string, endsAt: string) => void;
  submitting?: boolean;
}

function snapMinutes(dt: DateTime): DateTime {
  const snapped = Math.round(dt.minute / SNAP_MINUTES) * SNAP_MINUTES;
  if (snapped === 60) {
    return dt.plus({ hours: 1 }).set({ minute: 0, second: 0, millisecond: 0 });
  }
  return dt.set({ minute: snapped, second: 0, millisecond: 0 });
}

/**
 * Keyboard parity for drag-to-calendar: pick a start time, duration from estimate.
 */
export function ScheduleSheet({
  task,
  timezone,
  onClose,
  onSubmit,
  submitting,
}: Props) {
  const duration = task.estimate_minutes ?? SNAP_MINUTES * 2;
  const initial = useMemo(() => {
    const now = snapMinutes(DateTime.now().setZone(timezone).plus({ minutes: 15 }));
    return now.toFormat("yyyy-MM-dd'T'HH:mm");
  }, [timezone]);

  const [localStart, setLocalStart] = useState(initial);

  const preview = useMemo(() => {
    const start = DateTime.fromISO(localStart, { zone: timezone });
    if (!start.isValid) return null;
    const end = start.plus({ minutes: duration });
    return `${start.toFormat('ccc, LLL d · h:mm a')} – ${end.toFormat('h:mm a')} (${formatDuration(duration) ?? `${duration}m`})`;
  }, [localStart, timezone, duration]);

  return (
    <Sheet
      title="Schedule"
      onClose={onClose}
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            loading={submitting}
            onClick={() => {
              const start = DateTime.fromISO(localStart, { zone: timezone });
              if (!start.isValid) return;
              const end = start.plus({ minutes: duration });
              onSubmit(start.toUTC().toISO()!, end.toUTC().toISO()!);
            }}
          >
            Place block
          </Button>
        </div>
      }
    >
      <p className="mb-4 text-body text-text">{task.title}</p>
      <Field
        label="Start"
        type="datetime-local"
        step={SNAP_MINUTES * 60}
        value={localStart}
        onChange={(e) => setLocalStart(e.target.value)}
      />
      {preview && (
        <p className="mt-3 text-small text-text-secondary">{preview}</p>
      )}
      <p className="mt-4 text-small text-text-tertiary">
        Arrow keys move a selected block by {SNAP_MINUTES} minutes; Shift+arrows
        resize.
      </p>
    </Sheet>
  );
}
